import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, Check, Phone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { services } from '../mock';

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const others = services.filter((s) => s.slug !== slug).slice(0, 4);

  return (
    <div className="App bg-white">
      <Header />

      {/* Hero */}
      <section className="max-w-[1240px] mx-auto px-6 pt-6">
        <div className="relative rounded-[28px] overflow-hidden min-h-[420px]">
          <img src={service.hero} alt={service.name} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
          <div className="relative z-10 px-8 lg:px-14 pt-14 pb-14">
            <div className="flex items-center gap-2 text-[12px] text-white/80">
              <Link to="/" className="hover:text-white">Home</Link>
              <span>›</span>
              <Link to="/services" className="hover:text-white">Services</Link>
              <span>›</span>
              <span className="text-white font-medium">{service.name}</span>
            </div>
            <h1 className="mt-10 text-[42px] lg:text-[64px] leading-[0.98] font-bold tracking-[-0.03em] text-white max-w-2xl">
              {service.name}
            </h1>
            <p className="mt-4 text-white/90 text-[17px] max-w-xl">{service.tagline}</p>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1240px] mx-auto px-6 grid lg:grid-cols-[1fr_360px] gap-14">
          <div>
            <h2 className="text-[32px] lg:text-[42px] leading-[1.02] font-bold tracking-tight text-neutral-900">
              About the <span className="font-serif-italic font-normal">{service.name}</span> program
            </h2>
            <div className="mt-8 space-y-5 text-[16px] leading-relaxed text-neutral-700">
              {service.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            <h3 className="mt-12 text-[22px] font-semibold text-neutral-900">What&rsquo;s included</h3>
            <ul className="mt-4 space-y-3">
              {service.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-[#CA0028]/10 grid place-items-center shrink-0">
                    <Check size={12} className="text-[#CA0028]" />
                  </span>
                  <span className="text-[15px] text-neutral-700">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap gap-3">
              <a href="tel:+15192522752" className="inline-flex items-center gap-2 bg-[#CA0028] hover:bg-[#A80021] text-white text-[14px] font-medium pl-6 pr-2 py-2 rounded-full transition-colors">
                Call to Book This Service
                <span className="w-8 h-8 grid place-items-center rounded-full bg-white/15">
                  <ArrowRight size={14} />
                </span>
              </a>
              <a href="tel:+15192522752" className="inline-flex items-center gap-2 border border-neutral-300 text-neutral-900 text-[14px] font-medium px-5 py-2.5 rounded-full hover:border-[#CA0028] transition-colors">
                <Phone size={14} /> +1-519-252-2752
              </a>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:pt-6">
            <div className="rounded-2xl border border-neutral-200 p-6 bg-neutral-50">
              <h4 className="text-[13px] font-semibold tracking-[0.15em] text-neutral-500 uppercase">Good to know</h4>
              <div className="mt-4 space-y-4 text-[14px] text-neutral-700">
                <p><span className="font-medium text-neutral-900">Initial visit:</span> 60 minutes assessment + treatment.</p>
                <p><span className="font-medium text-neutral-900">Follow-ups:</span> 30–45 minutes based on plan.</p>
                <p><span className="font-medium text-neutral-900">Direct billing:</span> Available when supported by your plan and insurer.</p>
                <p><span className="font-medium text-neutral-900">Referral:</span> Not required for physiotherapy treatment, though some benefit plans may require one for reimbursement.</p>
              </div>
            </div>

            <div className="mt-6 rounded-2xl border border-neutral-200 p-6">
              <h4 className="text-[13px] font-semibold tracking-[0.15em] text-neutral-500 uppercase">You may also like</h4>
              <ul className="mt-3 divide-y divide-neutral-200">
                {others.map((o) => (
                  <li key={o.slug}>
                    <Link to={`/services/${o.slug}`} className="flex items-center justify-between py-3 text-[14px] text-neutral-900 hover:text-[#CA0028] transition-colors">
                      {o.name}
                      <ArrowRight size={14} />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceDetailPage;
